// apps/intranet/src/auth/useUserPreferences.ts
// ──────────────────────────────────────────────────────────────────────────────
// Per-user UI preferences. Generic key-value store — first consumer is the
// 'landingModule' preference (Settings → Startseite). Future keys (e.g.
// sidebar.collapsed-default) can land here without schema churn.
// ──────────────────────────────────────────────────────────────────────────────

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface UserPreferences {
  preferences: Record<string, string>
}

async function fetchPreferences(): Promise<UserPreferences> {
  const res = await fetch('/api/me/preferences', { credentials: 'include' })
  if (!res.ok) throw new Error(`/api/me/preferences ${res.status}`)
  return res.json()
}

async function setPreference(key: string, value: string | null): Promise<void> {
  const method = value === null ? 'DELETE' : 'PUT'
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  }
  if (value !== null) init.body = JSON.stringify({ value })
  const res = await fetch(`/api/me/preferences/${encodeURIComponent(key)}`, init)
  if (!res.ok) throw new Error(`/api/me/preferences/${key} ${res.status}`)
}

export function useUserPreferences() {
  const q = useQuery({
    queryKey: ['me-preferences'],
    queryFn: fetchPreferences,
    staleTime: 30_000,
  })

  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string | null }) => setPreference(key, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me-preferences'] })
    },
  })

  // Defensive: tolerate a malformed payload (missing `preferences`, or a
  // non-object) without throwing during render. Returning null forces
  // callers down the "no preference set" branch.
  const get = (key: string): string | null => {
    const prefs = q.data?.preferences
    if (!prefs || typeof prefs !== 'object') return null
    const v = (prefs as Record<string, unknown>)[key]
    return typeof v === 'string' ? v : null
  }
  const set = (key: string, value: string | null) => mutation.mutateAsync({ key, value })

  return {
    isLoading: q.isLoading,
    isError: q.isError,
    preferences: q.data?.preferences ?? {},
    get,
    set,
    saving: mutation.isPending,
  }
}
